import type { AxiosError, AxiosResponse } from 'axios';

import { logOut } from '@/helpers/auth';
import { toast } from '@flash-global66/g-toast';
// eslint-disable-next-line local-rules/architecture-boundaries -- deuda preexistente: sacarla exige mover ErrorsCommon a core/, fuera del alcance de este PR
import { ErrorsCommon } from '@/shared/common/errors';
import { handleAxiosError } from '@/helpers/error';
import type { GAxiosError } from '@/types/errorType';

/**
 * Endpoints cuyo 401 es una respuesta normal del negocio y no una sesion
 * vencida. Se comparan por igualdad exacta, y `core/` no puede importar de
 * `shared/`, asi que estas rutas van duplicadas a proposito.
 *
 * De webauthn estan las dos **publicas** del login, que se llaman sin sesion y
 * no tienen ninguna que cerrar.
 *
 * El snooze del recordatorio y `registration-options` si corren con sesion, y
 * estan aca igual: omitir una invitacion no puede costarla, y el 401 de las
 * opciones es el grant del login vencido, que la invitacion ya explica.
 *
 * El `DELETE` de una credencial lleva el id en la ruta y no entra por igualdad:
 * se exime por metodo y prefijo, porque su 401 es un PIN u OTP incorrecto. Las
 * que si llegan a `logOut()` son `registration-verify` y el `GET` del listado:
 * corren con sesion y un 401 significa que se vencio.
 *
 * El `PUT` de PIN tambien se exime por metodo: en el cambio su 401 es un PIN
 * actual incorrecto, y en el reseteo (sin `oldPin`) el workflow de Jumio
 * rechazado. El `POST` de alta de PIN sigue cerrando la sesion.
 */
const handleUnauthorized = (error: AxiosError) => {
  const excludeEndpoints = [
    'auth-server/b2b/auth/login',
    'auth-server/b2b/otp',
    'auth-server/ext/otp/validate',
    'auth-server/pin',
    'customer/b2b/onboarding/validate-otp',
    'beneficiary/b2b',
    'auth-server/b2b/session/refresh',
    'company/b2b/executive',
    'auth-server/b2b/auth/logout',
    'card/b2b/web-secure-token',
    'company/ext/password/forgot/confirm',
    'company/ext/onboarding/sing-up/confirm/manual',
    'auth-server/ext/webauthn/authentication-options',
    'auth-server/ext/webauthn/authentication-verify',
    'auth-server/b2b/auth/webauthn/enrollment-reminder/snooze',
    'auth-server/b2b/auth/webauthn/registration-options',
  ];

  const headers = error?.config?.headers;
  const hasCode = headers?.authvalue?.length || headers?.authValue?.length;

  const e = error as GAxiosError;
  const requestUrl = error?.config?.url || '';
  const isPinChange =
    error?.config?.method?.toLowerCase() === 'put' &&
    requestUrl === 'auth-server/b2b/pin';
  const isExcludedUrl = excludeEndpoints.includes(requestUrl) || isPinChange;
  const isPasskeyDelete =
    error?.config?.method?.toLowerCase() === 'delete' &&
    requestUrl.startsWith('auth-server/b2b/auth/webauthn/credentials/');
  const mappedReasons = Object.keys(ErrorsCommon);

  if (
    !isExcludedUrl &&
    mappedReasons.includes(e.response?.data?.reason || '')
  ) {
    const errorMessage = handleAxiosError({
      error,
      errorMap: ErrorsCommon as unknown as Record<string, string>,
    });

    toast.error(errorMessage);
  }

  if (isExcludedUrl || isPasskeyDelete || Boolean(hasCode)) {
    return;
  }
  logOut();
};

export const responseInterceptor = (response: AxiosResponse): AxiosResponse => {
  return response;
};

export async function responseErrorInterceptor(
  error: AxiosError
): Promise<AxiosError> {
  if (!error.response) {
    return Promise.reject(error);
  }
  const { status } = error.response;

  switch (status) {
    case 401:
      handleUnauthorized(error);
      break;
    default:
      break;
  }

  return Promise.reject(error);
};
